import { Popover } from './ui/Popover';
import { OptionPicker } from './ui/OptionPicker';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { useState } from 'react';
import styles from './FilterChips.module.css';
import iconCategory from '@/assets/icons/chip-category.svg';
import iconCity from '@/assets/icons/chip-city.svg';
import iconSku from '@/assets/icons/chip-sku.svg';

export type ChipKind = 'category' | 'city' | 'sku';

export interface Chip {
  kind: ChipKind;
  /** What the chip reads. */
  label: string;
  /** Currently selected raw value, or undefined when the level is unset. */
  value?: string | undefined;
}

const ICON: Record<ChipKind, string> = { category: iconCategory, city: iconCity, sku: iconSku };
const ICON_CLASS: Record<ChipKind, string> = {
  category: styles.chipIconCategory,
  city: styles.chipIconCity,
  sku: styles.chipIconSku,
};

const ALL_LABEL: Record<ChipKind, string> = {
  category: 'All Categories',
  city: 'All Cities',
  sku: 'All SKUs',
};

export function FilterChips({
  chips,
  customerName,
  onSelect,
  onClear,
  format,
}: {
  chips: Chip[];
  customerName: string;
  onSelect: (kind: ChipKind, value: string) => void;
  onClear: (kind: ChipKind) => void;
  /** Presentation mapper — cities are stored lower-cased, shown capitalised. */
  format?: Partial<Record<ChipKind, (label: string) => string>>;
}) {
  return (
    <div className={styles.bar} data-testid="filter-chips">
      <div className={styles.inner}>
        <span className={styles.legend}>Showing results for</span>
        <div className={styles.chips}>
          {chips.map((chip) => (
            <ChipPicker
              key={chip.kind}
              chip={chip}
              customerName={customerName}
              onSelect={onSelect}
              onClear={onClear}
              {...(format?.[chip.kind] ? { format: format[chip.kind] } : {})}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ChipPicker({
  chip,
  customerName,
  onSelect,
  onClear,
  format,
}: {
  chip: Chip;
  customerName: string;
  onSelect: (kind: ChipKind, value: string) => void;
  onClear: (kind: ChipKind) => void;
  format?: ((label: string) => string) | undefined;
}) {
  const [open, setOpen] = useState(false);
  // Options load only once the picker is opened — the page must not pay for
  // three option lists nobody asked for.
  const options = useFilterOptions(chip.kind, customerName, open);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={({ toggle }) => (
        <button type="button" className={styles.chip} onClick={toggle} aria-haspopup="listbox">
          <img className={ICON_CLASS[chip.kind]} src={ICON[chip.kind]} alt="" />
          {chip.label}
        </button>
      )}
    >
      {({ close }) => (
        <OptionPicker
          options={options.data ?? []}
          value={chip.value}
          loading={options.isLoading}
          {...(options.error ? { error: options.error.message } : {})}
          allLabel={ALL_LABEL[chip.kind]}
          {...(format ? { format } : {})}
          onSelect={(v) => { onSelect(chip.kind, v); close(); }}
          onClear={() => { onClear(chip.kind); close(); }}
        />
      )}
    </Popover>
  );
}
